import { useState, useEffect } from "react";
import {
  signin,
  signup,
  verify2faCode,
  forgotPassword,
  resetPassword,
  signout,
} from "../api/users";
import { getUserData } from "../api/get/get-user";
import { isTokenExpired } from "../utils/isTokenExpired";
import { jwtDecode } from "../utils/jwtDecode";

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [signIn, setSignIn] = useState(false);
  const [sign2fa, setSign2fa] = useState(false);
  const [signUp, setSignUp] = useState(false);
  const [meetScheduled, setMeetScheduled] = useState(false);
  const [mailSent, setMailSent] = useState(false);
  const [email, setEmail] = useState("");
  const [passwordReset, setPasswordReset] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [userId, setUserId] = useState(null);
  const [d_id, setD_id] = useState(null);
  const [customer_id, setCustomer_Id] = useState(null);
  const [upgradePlan, setUpgradePlan] = useState(0);
  const [token, setToken] = useState();
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [error, setError] = useState(null);

  const getSignin = async (credentials) => {
    setLoadingAuth(true);
    setError(null);
    try {
      const response = await signin(credentials);
      if (response.error) throw new Error(response.error);
      if (response.mfa_required === false) {
        mfaProcess(response);
        return response;
      }

      if (response.token) {
        sessionStorage.setItem("authToken2fa", response.token);
        setSign2fa(true);
        setSignIn(false);
      }

      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoadingAuth(false);
    }
  };

  const getVerify = async (enteredCode) => {
    if (!enteredCode) {
      throw new Error("Code is required");
    }

    const token = sessionStorage.getItem("authToken2fa");
    if (!token) {
      throw new Error("No 2fa token found");
    }

    const decodedToken = jwtDecode(token);
    const userId = decodedToken.id || decodedToken.payload?.id;
    if (!userId) {
      throw new Error("User ID not found in token");
    }

    setLoadingAuth(true);
    setError(null);
    try {
      const response = await verify2faCode(userId, enteredCode);
      sessionStorage.removeItem("authToken2fa");
      // console.log('response verify', response);
      mfaProcess(response);
      setSignIn(true);
      setSign2fa(false);
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoadingAuth(false);
    }
  };

  const normalizeResponse = (response) => {
    return {
      token: response.token || null,
      user: response.user
    };
  };

  const mfaProcess = async (response) => {
    const { token, user } = normalizeResponse(response);
    sessionStorage.setItem("authToken", token);
    sessionStorage.setItem("user", JSON.stringify(user));
    sessionStorage.setItem("userId", user.id);
    sessionStorage.setItem("d_id", user.d_id);
    sessionStorage.setItem("customer_id", user.customer_id);
    sessionStorage.setItem("plan", user.upgrade_plan);

    setUser(user);
    setD_id(user.d_id);
    setCustomer_Id(user.customer_id);
    setUserId(user.id);
    setUpgradePlan(user.upgrade_plan);

    setSignIn(true);
    setSign2fa(false);
  };

  const getSignup = async (data, uuid, token) => {
    console.log('data', data);
    console.log('uuid', uuid);
    console.log('token', token);
    setLoadingAuth(true);
    setError(null);
    try {
      const response = await signup(data, uuid, token);
      if (response.token) {
        sessionStorage.setItem("authToken2fa", response.token);
      }
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoadingAuth(false);
    }
  };

  const sendForgotEmail = async (email) => {
    if (!email) {
      setError("Email is required");
      return;
    }
    setLoadingAuth(true);
    setError(null);
    try {
      const response = await forgotPassword(email);
      // if (response.error) throw new Error(response.error);
      setMailSent(true);
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoadingAuth(false);
    }
  };

  const setResetPassword = async (userId, pass, cpass, token) => {
    if (!userId || !pass || !cpass || !token) {
      setError("Missing required fields");
      return;
    }

    if (pass !== cpass) {
      setError("Passwords do not match");
      return;
    }

    setLoadingAuth(true);
    setError(null);
    try {
      const response = await resetPassword(userId, pass, token);
      // if (response.error) throw new Error(response.error);
      setPasswordReset(true);
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoadingAuth(false);
    }
  };

  const getSignout = async () => {
    try {
      if (signIn) await signout(userId);
    } catch (error) {
      // A stale or expired token must not prevent the local session from being
      // cleared. The server-side sign-out is best-effort; client cleanup is not.
      console.error("Logout Error:", error);
    } finally {
      setUser(null);
      setSignIn(false);
      setSign2fa(false);
      setSignUp(false);
      setUserId(null);
      setD_id(null);
      setCustomer_Id(null);
      setUpgradePlan(0);
      setToken(null);
      setLoadingAuth(false);
      setError(null);
      sessionStorage.clear();
    }
  };

  const getUser = async (userIdArg) => {
    try {
      const response = await getUserData(userIdArg);
      mfaProcess(response);
    } catch (error) {
      // console.error("Error fetching user:", error);
    }
  };

  const validateSession = () => {
    const tokenMfa = sessionStorage.getItem("authToken2fa");
    const token = sessionStorage.getItem("authToken");
    const userData = JSON.parse(sessionStorage.getItem("user"));
    const userId = sessionStorage.getItem("userId");
    const plan = sessionStorage.getItem("plan");

    if (tokenMfa) {
      setSign2fa(true);
      return;
    }

    if (isTokenExpired(token)) {
      getSignout();
      return;
    }

    setSignIn(true);
    setToken(token);
    setUser(userData);
    setCustomer_Id(userData?.customer_id);
    setUserId(userId);
    setD_id(userData?.d_id);
    setUpgradePlan(plan);
  };

  return {
    user,
    setUser,
    signIn,
    setSignIn,
    sign2fa,
    setSign2fa,
    signUp,
    setSignUp,
    meetScheduled,
    setMeetScheduled,
    email,
    setEmail,
    passwordReset,
    setPasswordReset,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    mailSent,
    setMailSent,
    userId,
    setUserId,
    d_id,
    setD_id,
    customer_id,
    setCustomer_Id,
    token,
    setToken,
    loadingAuth,
    setLoadingAuth,
    upgradePlan,
    setUpgradePlan,
    getSignin,
    getSignup,
    getVerify,
    sendForgotEmail,
    setResetPassword,
    getSignout,
    getUser,
    mfaProcess,
    isTokenExpired,
    validateSession,
    error,
  };
};

export default useAuth;
